import VerifiedIcon from "@mui/icons-material/Verified";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";

import ProjectFinalApprovalApi from "../../../../../infrastructure/api/projectFinalApproval";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const APPROVAL_STATUS_APPROVED = 2;

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const isApiResultEnvelope = (raw) =>
  raw &&
  typeof raw === "object" &&
  Object.prototype.hasOwnProperty.call(raw, "isSuccess") &&
  raw.projectContractId == null &&
  raw.ProjectContractId == null;

const normalizeWorkspaceItem = (raw) => {
  if (!raw || isApiResultEnvelope(raw)) return null;
  const projectContractId = pick(raw, "projectContractId", "ProjectContractId");
  if (!projectContractId) return null;
  return {
    projectFinalApprovalId: pick(raw, "projectFinalApprovalId", "ProjectFinalApprovalId"),
    projectContractId,
    requestId: pick(raw, "requestId", "RequestId"),
    requestReferenceId: pick(raw, "requestReferenceId", "RequestReferenceId") ?? "",
    contractTitle: pick(raw, "contractTitle", "ContractTitle") ?? "",
    price: pick(raw, "price", "Price"),
    approvalStatus: pick(raw, "approvalStatus", "ApprovalStatus"),
    approvalStatusTitle: pick(raw, "approvalStatusTitle", "ApprovalStatusTitle") ?? "",
    projectContractStatusTitle:
      pick(raw, "projectContractStatusTitle", "ProjectContractStatusTitle") ?? "",
    technicalApproval: pick(raw, "technicalApproval", "TechnicalApproval") === true,
    financialApproval: pick(raw, "financialApproval", "FinancialApproval") === true,
    legalApproval: pick(raw, "legalApproval", "LegalApproval") === true,
    notes: pick(raw, "notes", "Notes") ?? "",
    approvedAt: pick(raw, "approvedAt", "ApprovedAt"),
    approvedByUserName: pick(raw, "approvedByUserName", "ApprovedByUserName") ?? "",
  };
};

const formatContractLabel = (item) => {
  const title = String(item?.contractTitle ?? "").trim();
  if (title && title !== "undefined" && title !== "null") return title;
  const reference = String(item?.requestReferenceId ?? "").trim();
  if (reference && reference !== "undefined") return `درخواست ${reference}`;
  const id = item?.projectContractId;
  if (id) return `قرارداد #${id}`;
  return "قرارداد ثبت نشده";
};

const formatFinalAmountLabel = (item) => {
  const amount = item?.price;
  const num = Number(amount);
  if (Number.isFinite(num) && num > 0) {
    return `${num.toLocaleString("fa-IR")} ریال`;
  }
  return "مبلغ نهایی ثبت نشده";
};

const formatApprovalResult = (approved) => (approved ? "تأیید شده" : "تأیید نشده");

const isFinalApprovalSubmitted = (item) =>
  Number(item?.approvalStatus) === APPROVAL_STATUS_APPROVED;

const WorkspaceFinalApprovalPanel = ({ requestId, source, hasFinalApproval, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [workspaceItem, setWorkspaceItem] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    technicalApproval: true,
    financialApproval: true,
    legalApproval: true,
    notes: "",
  });

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError("");
    try {
      const res = await ProjectFinalApprovalApi.getFinalApprovalWorkspaceByRequest(
        source,
        requestId
      );
      if (res.ok && res.data) {
        setWorkspaceItem(normalizeWorkspaceItem(res.data));
      } else {
        setWorkspaceItem(null);
      }
    } catch {
      setError("خطا در بارگذاری وضعیت تأیید نهایی.");
      setWorkspaceItem(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, source]);

  useEffect(() => {
    load();
  }, [load]);

  const submitted = workspaceItem
    ? isFinalApprovalSubmitted(workspaceItem)
    : hasFinalApproval === true;

  const handleApprove = async () => {
    if (!workspaceItem?.projectContractId) return;
    if (!form.technicalApproval || !form.financialApproval || !form.legalApproval) {
      setError("هر سه مورد فنی، مالی و حقوقی باید تیک بخورند.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await ProjectFinalApprovalApi.approveProjectFinal(source, {
        projectContractId: workspaceItem.projectContractId,
        technicalApproval: form.technicalApproval,
        financialApproval: form.financialApproval,
        legalApproval: form.legalApproval,
        notes: form.notes.trim() || undefined,
      });
      if (res.ok) {
        setSuccess(res.message || "تأیید نهایی پروژه ثبت شد.");
        await load();
        onChanged?.();
      } else {
        setError(res.message || "خطا در ثبت تأیید نهایی.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">بررسی وضعیت تأیید نهایی…</Typography>
        </Stack>
      </Paper>
    );
  }

  if (!workspaceItem && !submitted) {
    return null;
  }

  if (submitted) {
    const item = workspaceItem ?? {};
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: "success.50" }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
            <VerifiedIcon color="success" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              تأیید نهایی پروژه
            </Typography>
            <Chip size="small" color="success" label="تأیید نهایی ثبت شده" />
          </Stack>

          {success ? <Alert severity="success">{success}</Alert> : null}

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    قرارداد
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatContractLabel(item)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    مبلغ نهایی
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatFinalAmountLabel(item)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    تأیید فنی
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatApprovalResult(item.technicalApproval)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    تأیید مالی
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatApprovalResult(item.financialApproval)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    تأیید حقوقی
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatApprovalResult(item.legalApproval)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    تأییدکننده
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {item.approvedByUserName || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    تاریخ تأیید
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {item.approvedAt ? toJalaliFullDate(item.approvedAt) : "—"}
                  </Typography>
                </Grid>
                {item.notes ? (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      یادداشت
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "pre-wrap" }}>
                      {item.notes}
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 1.5,
        borderColor: "primary.main",
        bgcolor: "primary.50",
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <VerifiedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            تأیید نهایی پروژه
          </Typography>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <Alert severity="info">
          قرارداد اجرا ایجاد شده و منتظر تأیید نهایی است. این مرحله در مسیر جدید (قیمت
          مشتری) از همین workspace انجام می‌شود.
        </Alert>

        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip size="small" label={`قرارداد: ${formatContractLabel(workspaceItem)}`} />
          <Chip size="small" variant="outlined" label={`مبلغ: ${formatFinalAmountLabel(workspaceItem)}`} />
          {workspaceItem.projectContractStatusTitle ? (
            <Chip
              size="small"
              variant="outlined"
              label={workspaceItem.projectContractStatusTitle}
            />
          ) : null}
        </Stack>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.technicalApproval}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, technicalApproval: e.target.checked }))
                }
              />
            }
            label="تأیید فنی"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.financialApproval}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, financialApproval: e.target.checked }))
                }
              />
            }
            label="تأیید مالی"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.legalApproval}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, legalApproval: e.target.checked }))
                }
              />
            }
            label="تأیید حقوقی"
          />
        </FormGroup>

        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="یادداشت (اختیاری)"
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
        />

        <Button
          variant="contained"
          color="success"
          disabled={actionLoading}
          onClick={handleApprove}
        >
          ثبت تأیید نهایی پروژه
        </Button>
      </Stack>
    </Paper>
  );
};

WorkspaceFinalApprovalPanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  source: PropTypes.string.isRequired,
  hasFinalApproval: PropTypes.bool,
  onChanged: PropTypes.func,
};

export default WorkspaceFinalApprovalPanel;
